import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, Typography, Button, Box, Paper, IconButton, CircularProgress, TextField, InputAdornment, Accordion, AccordionSummary, AccordionDetails, Menu, MenuItem } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { getAllWikiEntries } from '../../services/wikiService';
import { getWikiIcon } from '../../utils/wikiLinkUtils';
import Breadcrumbs from '../Common/Breadcrumbs';
import AudioPlayer from './AudioPlayer';

// Configure PDF.js worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

/**
 * Session Storage State Management
 * 
 * This component automatically saves and restores the PDF viewer state using sessionStorage.
 * When you navigate away and come back, the following state is preserved:
 * - Current page number
 * - Zoom level (scale)
 * - Active search query
 * - Current search match index
 * 
 * The state is:
 * - Stored per PDF (using entryId as key)
 * - Persists for the browser tab session
 * - Automatically expires after 24 hours
 * - Does not interfere with search navigation from other pages
 * 
 * This provides a seamless "back button" experience where users return to exactly
 * where they left off in the PDF.
 */

// Helper functions for session storage
const SESSION_STORAGE_KEY = 'pdfViewerState';

const saveViewerState = (entryId, state) => {
  try {
    const allStates = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || '{}');
    allStates[entryId] = {
      ...state,
      timestamp: Date.now()
    };
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(allStates));
  } catch (error) {
    console.error('Error saving PDF viewer state:', error);
  }
};

const loadViewerState = (entryId) => {
  try {
    const allStates = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || '{}');
    const state = allStates[entryId];
    
    // Check if state exists and is less than 24 hours old
    if (state && (Date.now() - state.timestamp) < 24 * 60 * 60 * 1000) {
      return state;
    }
    return null;
  } catch (error) {
    console.error('Error loading PDF viewer state:', error);
    return null;
  }
};

// Optional: Clear state for a specific PDF (unused but available for future features)
// eslint-disable-next-line no-unused-vars
const clearViewerState = (entryId) => {
  try {
    const allStates = JSON.parse(sessionStorage.getItem(SESSION_STORAGE_KEY) || '{}');
    delete allStates[entryId];
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(allStates));
  } catch (error) {
    console.error('Error clearing PDF viewer state:', error);
  }
};

const PdfViewerPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { entryId: urlEntryId } = useParams();
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageMatches, setPageMatches] = useState({});
  const [scale, setScale] = useState(1.0);
  const [highlightPositions, setHighlightPositions] = useState([]);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [allMatches, setAllMatches] = useState([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [wikiEntries, setWikiEntries] = useState([]);
  const [detectedWikiEntries, setDetectedWikiEntries] = useState([]);
  
  // Track if we've already found the match page for initial search
  const hasFoundMatchPageRef = useRef(false);
  const [journalEntry, setJournalEntry] = useState(null);
  const [loadingEntry, setLoadingEntry] = useState(false);
  const [anchorEls, setAnchorEls] = useState({}); // For managing multiple dropdown menus
  const pageRef = useRef(null);
  const currentPageRef = useRef(currentPage);

  // Keep ref in sync with state
  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // Fetch journal entry if coming from URL
  useEffect(() => {
    const fetchJournalEntry = async () => {
      if (urlEntryId && !location.state?.pdfUrl) {
        setLoadingEntry(true);
        try {
          const { getJournalEntryById } = await import('../../services/bennyJournalService');
          const entry = await getJournalEntryById(urlEntryId);
          setJournalEntry(entry);
        } catch (error) {
          console.error('Error loading journal entry:', error);
          setJournalEntry(null);
        } finally {
          setLoadingEntry(false);
        }
      }
    };
    fetchJournalEntry();
  }, [urlEntryId, location.state?.pdfUrl]);

  const pdfUrlFromState = location.state?.pdfUrl || journalEntry?.downloadURL;
  const searchPhraseFromState = location.state?.searchPhrase;
  const matchNumber = location.state?.matchNumber || 1;
  const referrer = location.state?.from;

  // Determine the entry ID for session storage
  const entryId = urlEntryId || location.state?.journalTitle || 'default';

  // Track if we've already restored state to prevent re-restoration
  const hasRestoredRef = useRef(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Restore viewer state from sessionStorage on mount
  // This must run AFTER search initialization to avoid page conflicts
  useEffect(() => {
    // Only restore once and after hasInitialized is set, and not if coming from search navigation
    if (!hasRestoredRef.current && hasInitialized && !searchPhraseFromState) {
      const savedState = loadViewerState(entryId);
      if (savedState) {
        if (savedState.currentPage) setCurrentPage(savedState.currentPage);
        if (savedState.scale) setScale(savedState.scale);
        // Note: We skip restoring localSearchQuery to avoid triggering search effects
      }
      hasRestoredRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId, hasInitialized]); // Run after hasInitialized is set

  // Save viewer state to sessionStorage whenever key state changes
  useEffect(() => {
    // Only save if we have a PDF loaded and we've completed initial restoration
    if (pdfUrlFromState && hasRestoredRef.current) {
      const stateToSave = {
        currentPage,
        scale,
        localSearchQuery,
        currentMatchIndex,
        numPages
      };
      saveViewerState(entryId, stateToSave);
    }
  }, [currentPage, scale, localSearchQuery, currentMatchIndex, numPages, pdfUrlFromState, entryId]);

  // Perform local search across all pages
  const performLocalSearch = useCallback(async (searchQuery) => {
    if (!searchQuery || !pdfUrlFromState) {
      setAllMatches([]);
      setCurrentMatchIndex(0);
      return;
    }

    setIsSearching(true);
    try {
      const searchLower = searchQuery.toLowerCase();
      const matches = [];
      
      // Load the PDF document
      const loadingTask = pdfjs.getDocument(pdfUrlFromState);
      const pdfDocument = await loadingTask.promise;

      // Search through all pages to find matches
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        textContent.items.forEach((item, itemIndex) => {
          const text = item.str.toLowerCase();
          const originalText = item.str;
          
          // Find all occurrences of the search term within this text item
          let searchIndex = 0;
          let matchInItemCount = 0;
          while (searchIndex < text.length) {
            const foundIndex = text.indexOf(searchLower, searchIndex);
            if (foundIndex === -1) break;
            
            matchInItemCount++;
            const avgCharWidth = item.width / originalText.length;
            const textBeforeMatch = originalText.substring(0, foundIndex);
            const matchText = originalText.substring(foundIndex, foundIndex + searchLower.length);
            
            const matchStartX = item.transform[4] + (textBeforeMatch.length * avgCharWidth);
            const matchWidth = matchText.length * avgCharWidth;
            
            // Apply same adjustments for consistency
            const adjustedX = matchStartX - (avgCharWidth * 0.1);
            const adjustedWidth = matchWidth + (avgCharWidth * 0.2);
            
            matches.push({
              pageNumber: pageNum,
              text: matchText,
              position: {
                x: adjustedX,
                y: item.transform[5],
                width: adjustedWidth,
                height: item.height,
              },
              // Unique identifier for this match
              matchId: `page-${pageNum}-item-${itemIndex}-match-${matchInItemCount}`
            });
            
            searchIndex = foundIndex + searchLower.length;
          }
        });
      }

      setAllMatches(matches);
      setCurrentMatchIndex(0);
      
      // Navigate to first match if any found
      if (matches.length > 0) {
        const targetPage = matches[0].pageNumber;
        
        // If we're already on the target page, we need to manually update highlights
        // because setCurrentPage won't trigger a re-render
        if (targetPage === currentPageRef.current) {
          // Extract highlights for the current page from matches
          const pageHighlights = matches
            .filter(match => match.pageNumber === currentPageRef.current)
            .map((match, idx) => ({
              x: match.position.x,
              y: match.position.y,
              width: match.position.width,
              height: match.position.height,
              text: match.text,
              isCurrentMatch: idx === 0, // First match is current
              matchId: match.matchId
            }));
          setHighlightPositions(pageHighlights);
          setPageMatches(prev => ({ ...prev, [currentPageRef.current]: pageHighlights.length }));
        }
        
        setCurrentPage(targetPage);
      } else {
        // Clear highlights if no matches
        setHighlightPositions([]);
      }
    } catch (error) {
      console.error('Error performing local search:', error);
      setAllMatches([]);
      setCurrentMatchIndex(0);
    } finally {
      setIsSearching(false);
    }
  }, [pdfUrlFromState, numPages]);

  // Initialize local search with route state search phrase (only once)
  useEffect(() => {
    if (searchPhraseFromState && !hasInitialized) {
      setLocalSearchQuery(searchPhraseFromState);
      setHasInitialized(true);
    } else if (!searchPhraseFromState && !hasInitialized) {
      // If there's no search phrase, mark as initialized immediately
      setHasInitialized(true);
    }
  }, [searchPhraseFromState, hasInitialized]);

  // Trigger search when local search query changes and PDF is loaded (debounced)
  useEffect(() => {
    if (!localSearchQuery) {
      setAllMatches([]);
      setCurrentMatchIndex(0);
      return;
    }

    if (numPages > 0) {
      const timeoutId = setTimeout(() => {
        performLocalSearch(localSearchQuery);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [localSearchQuery, numPages, performLocalSearch]);

  // Fetch wiki entries on component mount
  useEffect(() => {
    const fetchWikiEntries = async () => {
      try {
        const entries = await getAllWikiEntries();
        setWikiEntries(entries || []);
      } catch (error) {
        console.error('Error fetching wiki entries:', error);
      }
    };
    fetchWikiEntries();
  }, []);

  // Extract full PDF text and detect wiki entries
  useEffect(() => {
    const extractTextAndDetectWiki = async () => {
      if (!pdfUrlFromState || numPages === 0 || wikiEntries.length === 0) {
        return;
      }

      try {
        const loadingTask = pdfjs.getDocument(pdfUrlFromState);
        const pdfDocument = await loadingTask.promise;
        let fullText = '';

        // Extract text from all pages
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          const page = await pdfDocument.getPage(pageNum);
          const textContent = await page.getTextContent();
          const pageText = textContent.items.map(item => item.str).join(' ');
          fullText += pageText + ' ';
        }

        // Detect which wiki entries are mentioned in the PDF
        const detected = [];

        wikiEntries.forEach(entry => {
          if (!entry.title) return;
          
          // Check if title appears in text
          const regex = new RegExp(`\\b${escapeRegex(entry.title)}\\b`, 'i');
          const titleFound = regex.test(fullText);
          
          // Check which nicknames appear in text
          const foundNicknames = [];
          if (entry.nicknames && entry.nicknames.trim()) {
            const nicknameList = entry.nicknames.split(',').map(n => n.trim()).filter(n => n);
            for (const nickname of nicknameList) {
              const nicknameRegex = new RegExp(`\\b${escapeRegex(nickname)}\\b`, 'i');
              if (nicknameRegex.test(fullText)) {
                foundNicknames.push(nickname);
              }
            }
          }
          
          // Add entry if either title or any nickname was found
          if (titleFound || foundNicknames.length > 0) {
            detected.push({
              ...entry,
              matchedKeyword: titleFound ? entry.title : foundNicknames[0],
              titleFoundInPdf: titleFound,
              foundNicknames: foundNicknames
            });
          }
        });

        // Sort by title
        detected.sort((a, b) => a.title.localeCompare(b.title));
        setDetectedWikiEntries(detected);
        
        if (detected.length > 0) {
          console.log('Detected wiki entries in PDF:', detected.map(e => e.title));
        }
      } catch (error) {
        console.error('Error extracting PDF text:', error);
      }
    };

    extractTextAndDetectWiki();
  }, [pdfUrlFromState, numPages, wikiEntries]);

  // Helper function to escape regex special characters
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // When PDF loads successfully
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  // Extract text and find highlight positions
  const onPageLoadSuccess = useCallback(async (page) => {
    const activeSearchQuery = localSearchQuery || searchPhraseFromState;
    if (!activeSearchQuery) {
      setHighlightPositions([]);
      return;
    }

    try {
      const textContent = await page.getTextContent();
      const searchLower = activeSearchQuery.toLowerCase();
      const positions = [];
      let matchCount = 0;

      let pageMatchCount = 0; // Track matches on this specific page
      
      textContent.items.forEach((item, itemIndex) => {
        const text = item.str.toLowerCase();
        const originalText = item.str;
        
        // Find all occurrences of the search term within this text item
        let searchIndex = 0;
        let matchInItemCount = 0;
        while (searchIndex < text.length) {
          const foundIndex = text.indexOf(searchLower, searchIndex);
          if (foundIndex === -1) break;
          
          pageMatchCount++;
          matchInItemCount++;
          
          // Calculate more precise positioning for just the matched text
          const transform = item.transform;
          
          // More accurate character positioning using text before the match
          const textBeforeMatch = originalText.substring(0, foundIndex);
          const matchText = originalText.substring(foundIndex, foundIndex + searchLower.length);
          
          // Estimate character positions more accurately
          const avgCharWidth = item.width / originalText.length;
          const matchStartX = transform[4] + (textBeforeMatch.length * avgCharWidth);
          const matchWidth = matchText.length * avgCharWidth;
          
          // Adjust for font scaling and spacing
          const adjustedX = matchStartX - (avgCharWidth * 0.1); // Small left adjustment
          const adjustedWidth = matchWidth + (avgCharWidth * 0.2); // Slightly wider
          
          // Generate the same match ID as in the allMatches array
          const matchId = `page-${currentPage}-item-${itemIndex}-match-${matchInItemCount}`;
          
          // Initially set to false - will be updated by separate useEffect
          const isCurrentMatch = false;
          
          positions.push({
            x: adjustedX,
            y: transform[5],
            width: adjustedWidth,
            height: item.height,
            text: originalText.substring(foundIndex, foundIndex + searchLower.length),
            isCurrentMatch: isCurrentMatch,
            matchId: matchId
          });
          
          searchIndex = foundIndex + searchLower.length;
        }
      });

      matchCount = pageMatchCount;

      setPageMatches(prev => ({ ...prev, [currentPage]: matchCount }));
      setHighlightPositions(positions);
    } catch (error) {
      console.error('Error extracting text for highlights:', error);
      setHighlightPositions([]);
    }
  }, [localSearchQuery, searchPhraseFromState, currentPage]);

  // Function to find which page contains the specified match
  const findMatchPage = useCallback(async () => {
    if (!searchPhraseFromState || !pdfUrlFromState) return;

    try {
      const searchLower = searchPhraseFromState.toLowerCase();
      let totalMatchCount = 0;
      let targetPage = 1;

      // Load the PDF document
      const loadingTask = pdfjs.getDocument(pdfUrlFromState);
      const pdfDocument = await loadingTask.promise;

      // Search through all pages to find matches
      for (let pageNum = 1; pageNum <= numPages; pageNum++) {
        const page = await pdfDocument.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        let pageMatchCount = 0;
        textContent.items.forEach((item) => {
          const text = item.str.toLowerCase();
          if (text.includes(searchLower)) {
            pageMatchCount++;
          }
        });

        totalMatchCount += pageMatchCount;
        
        // Check if our target match number falls within this page's matches
        if (totalMatchCount >= matchNumber) {
          targetPage = pageNum;
          break;
        }
      }

      setCurrentPage(targetPage);
    } catch (error) {
      console.error('Error finding match page:', error);
      // Only fallback to page 1 if we don't have local matches active
      if (!localSearchQuery && allMatches.length === 0) {
        setCurrentPage(1);
      }
    }
  }, [searchPhraseFromState, numPages, pdfUrlFromState, matchNumber, localSearchQuery, allMatches.length]);

  // Reset highlighted pages when changing pages or search query
  useEffect(() => {
    setHighlightPositions([]);
  }, [currentPage, searchPhraseFromState, localSearchQuery]);

  // Separate effect to update current match styling without full regeneration
  useEffect(() => {
    if (highlightPositions.length > 0 && allMatches.length > 0) {
      setHighlightPositions(prevPositions => 
        prevPositions.map(pos => {
          const currentMatch = allMatches[currentMatchIndex];
          const isCurrentMatch = currentMatch && currentMatch.matchId === pos.matchId;
          return { ...pos, isCurrentMatch };
        })
      );
    }
  }, [currentMatchIndex, allMatches, highlightPositions.length]);



  // Find all matches across all pages and navigate to correct page
  // Only run for initial global search navigation (from SearchBar)
  useEffect(() => {
    if (searchPhraseFromState && numPages > 0 && !hasFoundMatchPageRef.current) {
      findMatchPage();
      hasFoundMatchPageRef.current = true;
    }
  }, [searchPhraseFromState, numPages, findMatchPage]);

  const handleBackNavigation = () => {
    if (referrer) {
      navigate(referrer);
    } else {
      navigate('/benny-journal');
    }
  };

  const getBackButtonText = () => {
    if (referrer?.includes('/campaign-episodes/')) {
      return 'Back to Episode Details';
    } else if (referrer === '/campaign-episodes') {
      return 'Back to Campaign Episodes';
    } else if (referrer?.includes('/benny-journal/')) {
      return 'Back to Journal Entry';
    } else if (referrer === '/dashboard') {
      return 'Back to Dashboard';
    } else {
      return 'Back to Journal List';
    }
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, numPages));
  };

  const goToPrevPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  // Navigate to next match
  const goToNextMatch = () => {
    if (allMatches.length === 0) return;
    const nextIndex = (currentMatchIndex + 1) % allMatches.length;
    setCurrentMatchIndex(nextIndex);
    setCurrentPage(allMatches[nextIndex].pageNumber);
  };

  // Navigate to previous match
  const goToPreviousMatch = () => {
    if (allMatches.length === 0) return;
    const prevIndex = currentMatchIndex === 0 ? allMatches.length - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);
    setCurrentPage(allMatches[prevIndex].pageNumber);
  };

  // Handle local search input change
  const handleLocalSearchChange = (event) => {
    const value = event.target.value;
    setLocalSearchQuery(value);
  };

  // Clear local search
  const clearLocalSearch = () => {
    setLocalSearchQuery('');
    setAllMatches([]);
    setCurrentMatchIndex(0);
    setHighlightPositions([]);
    setHasInitialized(true); // Prevent re-initialization after clearing
  };

  // Search for a wiki entry's matched keyword
  const searchForKeyword = (keyword) => {
    setLocalSearchQuery(keyword);
    setHasInitialized(true); // Prevent re-initialization
  };

  // Handle opening nickname dropdown menu
  const handleOpenMenu = (event, entryId) => {
    setAnchorEls(prev => ({ ...prev, [entryId]: event.currentTarget }));
  };

  // Handle closing nickname dropdown menu
  const handleCloseMenu = (entryId) => {
    setAnchorEls(prev => ({ ...prev, [entryId]: null }));
  };

  // Handle selecting a nickname to search
  const handleNicknameSearch = (nickname, entryId) => {
    searchForKeyword(nickname);
    handleCloseMenu(entryId);
  };

  // Navigate to wiki with breadcrumb trail
  const navigateToWiki = (entryId, entryTitle, entryType) => {
    const currentBreadcrumbs = location.state?.breadcrumbTrail || [];
    const currentPath = location.pathname;
    const journalTitle = location.state?.journalTitle || 'Benny\'s Journal';
    
    // Add current page to breadcrumb trail
    const newTrail = [
      ...currentBreadcrumbs,
      {
        label: journalTitle,
        path: currentPath,
        icon: '📜',
        state: location.state
      }
    ];
    
    navigate(`/wiki/${entryId}`, {
      state: {
        breadcrumbTrail: newTrail
      }
    });
  };



  return (
    <Container 
      maxWidth={false}
      disableGutters
      sx={{ 
        pt: 0, 
        pb: 2,
        px: 0,
        height: 'calc(100vh - 64px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f5f5dc',
        fontFamily: '"Georgia", serif',
      }}
    >
      {/* Breadcrumbs */}
      <Breadcrumbs 
        currentLabel={`${location.state?.journalTitle || journalEntry?.title || 'Viewing Scroll'}${numPages > 0 ? ` - Page ${currentPage} of ${numPages}` : ''}`}
        currentIcon="📜" 
      />
      
      {/* Audio Player - Show if journal entry has audio */}
      {(journalEntry?.audioURL || location.state?.audioURL) && (
        <Box sx={{ px: 2, pt: 2 }}>
          <AudioPlayer 
            audioURL={journalEntry?.audioURL || location.state?.audioURL}
            title={journalEntry?.audioFileName || location.state?.audioFileName || "Journal Reading"}
          />
        </Box>
      )}
      
      {/* Main Content Area */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'row',
        gap: 2,
        flex: 1,
        px: 2,
        pt: 2,
        overflow: 'hidden'
      }}>
      {/* PDF Viewer - Full Width */}
      <Box sx={{ 
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        height: '100%',
      }}>
      <Paper 
        elevation={3} 
        sx={{ 
          width: '100%',
          height: '100%',
          overflow: 'auto',
          border: '1px solid #d2b48c',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#525252',
          p: 2,
          // Style the text layer to be invisible but selectable
          '& .react-pdf__Page__textContent': {
            '& span': {
              color: 'transparent',
              opacity: 0.2,
            }
          }
        }}
      >
        {loadingEntry ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
            <CircularProgress sx={{ color: '#8B4513' }} />
            <Typography sx={{ ml: 2, color: '#f5f5f5', fontFamily: '"Georgia", serif' }}>
              Loading journal entry...
            </Typography>
          </Box>
        ) : pdfUrlFromState ? (
          <Document
            file={pdfUrlFromState}
            onLoadSuccess={onDocumentLoadSuccess}
            loading={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                <CircularProgress sx={{ color: '#8B4513' }} />
              </Box>
            }
            error={
              <Box sx={{ p: 4 }}>
                <Typography sx={{ color: '#f5f5f5', fontFamily: '"Georgia", serif' }}>
                  Failed to load PDF. The scroll may be corrupted.
                </Typography>
              </Box>
            }
          >
            <Box sx={{ position: 'relative', display: 'inline-block' }} ref={pageRef}>
              <Page
                pageNumber={currentPage}
                renderTextLayer={true}
                renderAnnotationLayer={false}
                onLoadSuccess={onPageLoadSuccess}
                scale={scale}
                loading={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
                    <CircularProgress sx={{ color: '#8B4513' }} size={24} />
                  </Box>
                }
              />
              {/* Custom highlight overlay - precise keyword highlighting */}
              {highlightPositions.map((pos, idx) => (
                <Box
                  key={idx}
                  sx={{
                    position: 'absolute',
                    left: `${pos.x * scale}px`,
                    bottom: `${pos.y * scale}px`,
                    width: `${pos.width * scale}px`,
                    height: `${pos.height * scale}px`,
                    backgroundColor: pos.isCurrentMatch 
                      ? 'rgba(255, 87, 34, 0.8)' // Orange/red for current match
                      : 'rgba(255, 235, 59, 0.6)', // Yellow for other matches
                    border: pos.isCurrentMatch 
                      ? '2px solid rgba(255, 87, 34, 1)' // Stronger border for current
                      : '1px solid rgba(255, 200, 0, 0.8)', // Regular border for others
                    borderRadius: '3px',
                    pointerEvents: 'none',
                    zIndex: pos.isCurrentMatch ? 2 : 1, // Current match appears above others
                    boxShadow: pos.isCurrentMatch 
                      ? '0 0 4px rgba(255, 87, 34, 0.6)' // Glow effect for current match
                      : 'none',
                    // Subtle pulsing animation for current match
                    ...(pos.isCurrentMatch && {
                      transition: 'all 0.3s ease-in-out',
                      transform: 'scale(1.05)', // Slightly larger
                    })
                  }}
                />
              ))}
            </Box>
          </Document>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', p: 4, gap: 2 }}>
            <Typography sx={{ color: '#f5f5f5', fontFamily: '"Georgia", serif', textAlign: 'center' }}>
              {urlEntryId && journalEntry && !journalEntry.downloadURL 
                ? "This journal entry doesn't have an attached PDF scroll."
                : urlEntryId && !journalEntry && !loadingEntry
                ? "Journal entry not found."
                : "No PDF URL provided."}
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/benny-journal')}
              sx={{
                fontFamily: '"Georgia", serif',
                backgroundColor: '#8B4513',
                color: '#F5F5F5',
                '&:hover': { backgroundColor: '#A0522D' }
              }}
            >
              Return to Journal List
            </Button>
          </Box>
        )}
      </Paper>
      </Box>

      {/* Control Sidebar */}
      <Paper 
        elevation={3}
        sx={{ 
          width: 300,
          flexShrink: 0,
          height: '100%',
          overflowY: 'auto',
          backgroundColor: '#fffacd',
          border: '2px solid #B08D57',
          borderRadius: 2,
          p: 2,
        }}
      >
        {/* Title */}
        <Typography 
          variant="h5" 
          sx={{ 
            fontFamily: '"Georgia", serif', 
            color: '#8B4513', 
            fontWeight: 'bold', 
            mb: 2,
            borderBottom: '2px solid #B08D57',
            pb: 1
          }}
        >
          📜 Scroll Controls
        </Typography>

        {/* Back Button */}
        <Button 
          variant="contained" 
          startIcon={<ArrowBackIcon />}
          fullWidth
          onClick={handleBackNavigation}
          sx={{
            fontFamily: '"Georgia", serif',
            backgroundColor: '#8B4513', 
            color: '#F5F5F5',
            '&:hover': { backgroundColor: '#A0522D' },
            mb: 3
          }}
        >
          {getBackButtonText()}
        </Button>

        {/* Search Section */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontFamily: '"Georgia", serif', 
              color: '#8B4513', 
              fontWeight: 'bold', 
              mb: 1 
            }}
          >
            🔍 Search in Document
          </Typography>
          <TextField
            size="small"
            placeholder="Search in PDF..."
            fullWidth
            value={localSearchQuery}
            onChange={handleLocalSearchChange}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
                backgroundColor: 'white',
                '& fieldset': { borderColor: '#8B4513' },
                '&:hover fieldset': { borderColor: '#A0522D' },
                '&.Mui-focused fieldset': { borderColor: '#8B4513' }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#8B4513', fontSize: '1rem' }} />
                </InputAdornment>
              ),
              endAdornment: localSearchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearLocalSearch}
                    sx={{ color: '#666', p: 0.5 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
          
          {/* Search Results */}
          {allMatches.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography 
                  variant="caption" 
                  sx={{ fontFamily: '"Georgia", serif', color: '#666' }}
                >
                  Match {currentMatchIndex + 1} of {allMatches.length}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={goToPreviousMatch}
                    disabled={allMatches.length === 0}
                    sx={{ 
                      backgroundColor: '#8B4513', 
                      color: '#fff',
                      '&:hover': { backgroundColor: '#A0522D' },
                      '&:disabled': { backgroundColor: '#ccc' },
                      width: 24,
                      height: 24
                    }}
                  >
                    <KeyboardArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={goToNextMatch}
                    disabled={allMatches.length === 0}
                    sx={{ 
                      backgroundColor: '#8B4513', 
                      color: '#fff',
                      '&:hover': { backgroundColor: '#A0522D' },
                      '&:disabled': { backgroundColor: '#ccc' },
                      width: 24,
                      height: 24
                    }}
                  >
                    <KeyboardArrowDownIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              {localSearchQuery && pageMatches[currentPage] > 0 && (
                <Typography 
                  variant="caption" 
                  sx={{ fontFamily: '"Georgia", serif', color: '#666', display: 'block' }}
                >
                  {pageMatches[currentPage]} on this page
                </Typography>
              )}
            </Box>
          )}
          
          {isSearching && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress sx={{ color: '#8B4513' }} size={16} />
              <Typography variant="caption" sx={{ fontFamily: '"Georgia", serif', color: '#666' }}>
                Searching...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Navigation Section */}
        {numPages > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontFamily: '"Georgia", serif', 
                color: '#8B4513', 
                fontWeight: 'bold', 
                mb: 1 
              }}
            >
              📄 Page Navigation
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography 
                variant="body2" 
                sx={{ fontFamily: '"Georgia", serif', color: '#666' }}
              >
                Page {currentPage} of {numPages}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  sx={{ 
                    backgroundColor: '#8B4513', 
                    color: '#fff',
                    '&:hover': { backgroundColor: '#A0522D' },
                    '&:disabled': { backgroundColor: '#ccc' },
                    width: 32,
                    height: 32
                  }}
                >
                  <NavigateBeforeIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={goToNextPage}
                  disabled={currentPage === numPages}
                  sx={{ 
                    backgroundColor: '#8B4513', 
                    color: '#fff',
                    '&:hover': { backgroundColor: '#A0522D' },
                    '&:disabled': { backgroundColor: '#ccc' },
                    width: 32,
                    height: 32
                  }}
                >
                  <NavigateNextIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* Zoom Section */}
        {numPages > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ 
                fontFamily: '"Georgia", serif', 
                color: '#8B4513', 
                fontWeight: 'bold', 
                mb: 1 
              }}
            >
              🔎 Zoom
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography 
                variant="body2" 
                sx={{ fontFamily: '"Georgia", serif', color: '#666' }}
              >
                {Math.round(scale * 100)}%
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton 
                  size="small" 
                  onClick={zoomOut}
                  disabled={scale <= 0.5}
                  sx={{ 
                    backgroundColor: '#8B4513', 
                    color: '#fff',
                    '&:hover': { backgroundColor: '#A0522D' },
                    '&:disabled': { backgroundColor: '#ccc' },
                    width: 32,
                    height: 32
                  }}
                >
                  <ZoomOutIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={resetZoom}
                  sx={{ 
                    backgroundColor: '#8B4513', 
                    color: '#fff',
                    '&:hover': { backgroundColor: '#A0522D' },
                    width: 32,
                    height: 32
                  }}
                >
                  <RestartAltIcon />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={zoomIn}
                  disabled={scale >= 3.0}
                  sx={{ 
                    backgroundColor: '#8B4513', 
                    color: '#fff',
                    '&:hover': { backgroundColor: '#A0522D' },
                    '&:disabled': { backgroundColor: '#ccc' },
                    width: 32,
                    height: 32
                  }}
                >
                  <ZoomInIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>
        )}

        {/* Wiki Entries Section */}
        {detectedWikiEntries.length > 0 && (
          <Box sx={{ 
            borderTop: '1px solid #B08D57',
            pt: 2,
            display: 'flex', 
            flexDirection: 'column', 
            gap: 1 
          }}>
            {/* Section Title */}
            <Typography
              sx={{
                fontFamily: '"Georgia", serif',
                fontSize: '1rem',
                fontWeight: 'bold',
                color: '#B08D57',
                mb: 1,
                px: 1,
              }}
            >
              📖 Found Wiki Keywords
            </Typography>
            
            {/* Group entries by type */}
            {(() => {
              const typeGroups = {
                playerCharacter: {
                  entries: detectedWikiEntries.filter(e => 
                    e.type === 'person' && e.tags && e.tags.includes('Player Character')
                  ),
                  label: 'Player Characters',
                  icon: '🎭',
                  color: '#B08D57',
                  hoverColor: '#9a7a4a'
                },
                location: { 
                  entries: detectedWikiEntries.filter(e => e.type === 'location'),
                  label: 'Locations',
                  icon: '📍',
                  color: '#42a5f5',
                  hoverColor: '#1e88e5'
                },
                person: { 
                  entries: detectedWikiEntries.filter(e => 
                    e.type === 'person' && (!e.tags || !e.tags.includes('Player Character'))
                  ),
                  label: 'People',
                  icon: '👥',
                  color: '#66bb6a',
                  hoverColor: '#4caf50'
                },
                item: { 
                  entries: detectedWikiEntries.filter(e => e.type === 'item'),
                  label: 'Items',
                  icon: '⚔️',
                  color: '#ffa726',
                  hoverColor: '#fb8c00'
                },
                monster: { 
                  entries: detectedWikiEntries.filter(e => e.type === 'monster'),
                  label: 'Monsters',
                  icon: '👹',
                  color: '#e91e63',
                  hoverColor: '#d81b60'
                },
                faction: { 
                  entries: detectedWikiEntries.filter(e => e.type === 'faction'),
                  label: 'Factions',
                  icon: '🏰',
                  color: '#ab47bc',
                  hoverColor: '#9c27b0'
                }
              };

              return (
                <>
                  {Object.entries(typeGroups).map(([type, group]) => {
                    if (group.entries.length === 0) return null;
                    
                    return (
                      <Accordion 
                        key={type}
                        sx={{
                          backgroundColor: group.color,
                          color: 'white',
                          '&:before': {
                            display: 'none',
                          },
                          boxShadow: 'none',
                          mb: 1,
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
                          sx={{
                            fontFamily: '"Georgia", serif',
                            minHeight: 40,
                            px: 2,
                            '&.Mui-expanded': {
                              minHeight: 40,
                            },
                            '& .MuiAccordionSummary-content': {
                              margin: '8px 0',
                              '&.Mui-expanded': {
                                margin: '8px 0',
                              },
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <span>{group.icon}</span>
                            <Typography 
                              sx={{ 
                                fontFamily: '"Georgia", serif',
                                fontSize: '0.85rem',
                                fontWeight: 'bold',
                              }}
                            >
                              {group.label} ({group.entries.length})
                            </Typography>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ 
                          p: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          backgroundColor: group.hoverColor,
                        }}>
                          {group.entries.map(entry => (
                          <Box
                            key={entry.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'stretch',
                              backgroundColor: 'transparent',
                              '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                              }
                            }}
                          >
                            <Button
                              onClick={() => navigateToWiki(entry.id, entry.title, entry.type)}
                              sx={{
                                fontFamily: '"Georgia", serif',
                                backgroundColor: 'transparent',
                                color: 'white',
                                textTransform: 'none',
                                justifyContent: 'flex-start',
                                px: 2,
                                py: 0.75,
                                fontSize: '0.8rem',
                                borderRadius: 0,
                                flex: 1,
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                <span>{getWikiIcon(entry.type)}</span>
                                <Typography 
                                  sx={{ 
                                    fontFamily: '"Georgia", serif',
                                    fontSize: '0.8rem',
                                    textAlign: 'left',
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {entry.title}
                                </Typography>
                              </Box>
                            </Button>
                            {((entry.titleFoundInPdf && entry.foundNicknames && entry.foundNicknames.length > 0) || 
                              (!entry.titleFoundInPdf && entry.foundNicknames && entry.foundNicknames.length > 1)) ? (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenMenu(e, entry.id);
                                  }}
                                  sx={{
                                    color: 'white',
                                    borderRadius: 0,
                                    px: 1,
                                    '&:hover': {
                                      backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                    }
                                  }}
                                  title={`Search for other names`}
                                >
                                  <SearchIcon sx={{ fontSize: '1rem' }} />
                                </IconButton>
                                <Menu
                                  anchorEl={anchorEls[entry.id]}
                                  open={Boolean(anchorEls[entry.id])}
                                  onClose={() => handleCloseMenu(entry.id)}
                                  PaperProps={{
                                    sx: {
                                      backgroundColor: '#9a7a4a',
                                      color: 'white',
                                    }
                                  }}
                                >
                                  {entry.titleFoundInPdf && (
                                    <MenuItem
                                      onClick={() => handleNicknameSearch(entry.title, entry.id)}
                                      sx={{
                                        fontFamily: '"Georgia", serif',
                                        fontSize: '0.8rem',
                                        '&:hover': {
                                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        }
                                      }}
                                    >
                                      {entry.title} (main name)
                                    </MenuItem>
                                  )}
                                  {entry.foundNicknames && entry.foundNicknames.map((nickname, idx) => (
                                    <MenuItem
                                      key={idx}
                                      onClick={() => handleNicknameSearch(nickname, entry.id)}
                                      sx={{
                                        fontFamily: '"Georgia", serif',
                                        fontSize: '0.8rem',
                                        '&:hover': {
                                          backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                        }
                                      }}
                                    >
                                      {nickname}
                                    </MenuItem>
                                  ))}
                                </Menu>
                              </>
                            ) : (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  searchForKeyword(entry.matchedKeyword);
                                }}
                                sx={{
                                  color: 'white',
                                  borderRadius: 0,
                                  px: 1,
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                  }
                                }}
                                title={`Search for "${entry.matchedKeyword}"`}
                              >
                                <SearchIcon sx={{ fontSize: '1rem' }} />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                </>
              );
            })()}
          </Box>
        )}
      </Paper>
      </Box>
      {/* End Main Content Area */}
    </Container>
  );
};

export default PdfViewerPage;
